import { z } from 'zod';
import { compararQuantidade, somarQuantidade } from './aritmetica.ts';
import { dataHoraSchema, dataSchema, tipoInsumoSchema, uuidSchema } from './comum.ts';
import {
  freteTemValor,
  propostaCotacaoEntradaSchema,
  propostaCotacaoSchema,
  type PropostaCotacao,
  type PropostaCotacaoEntrada,
} from './cotacoes-preco.ts';
import { situacaoEnvioSchema } from './envios-pedido-compra.ts';

/**
 * A cotação de preço respondida pelo fornecedor, por link e sem login
 * (`.spec/015`, decisão 117).
 *
 * O comprador manda a cotação a um ou mais fornecedores; cada um recebe por
 * e-mail um link com um token que identifica **a cotação e ele**. Pelo link, o
 * fornecedor vê o que se cota e grava a proposta dele, com os impostos — direto
 * na cotação, como se o comprador a tivesse digitado.
 *
 * **O link fecha sozinho**, e quem o fecha é a cotação, não uma data do link: o
 * primeiro pedido gerado dela (de qualquer proposta), o fim do dia do prazo de
 * resposta, ou a cotação desativada. Até lá o fornecedor altera a proposta
 * quantas vezes quiser. É por isso que o prazo passa a ser obrigatório para
 * mandar a cotação.
 *
 * **A cotação continua sendo de um insumo só** — é o desenho dela, e o link não
 * muda isso: cada cotação vai sozinha, com o material e a quantidade dela.
 */

/* -------------------------------------------------------------------------- */
/* O link está aberto?                                                        */
/* -------------------------------------------------------------------------- */

export const SITUACOES_CONVITE = [
  'ABERTO',
  'SUBSTITUIDO',
  'COM_PEDIDO',
  'PRAZO_VENCIDO',
  'SEM_PRAZO',
  'COTACAO_INATIVA',
] as const;
export const situacaoConviteSchema = z.enum(SITUACOES_CONVITE);
export type SituacaoConvite = z.infer<typeof situacaoConviteSchema>;

/** O que a página pública diz ao fornecedor, e o histórico ao comprador. */
export const ROTULO_SITUACAO_CONVITE: Readonly<Record<SituacaoConvite, string>> = {
  ABERTO: 'Aberto para resposta',
  SUBSTITUIDO: 'Substituído por um envio mais novo',
  COM_PEDIDO: 'Encerrada — o pedido de compra já foi feito',
  PRAZO_VENCIDO: 'Encerrada — o prazo de resposta terminou',
  SEM_PRAZO: 'Encerrada — a cotação está sem prazo de resposta',
  COTACAO_INATIVA: 'Encerrada — a cotação foi desativada',
};

/**
 * O estado do link, a partir do que a cotação é **agora**.
 *
 * Calculado na leitura, e nunca gravado: o prazo vence à meia-noite sem que
 * ninguém grave nada, e uma coluna "encerrado" teria de ser mantida por um
 * agendador que o sistema não tem. A ordem das perguntas é a do que o
 * fornecedor precisa saber primeiro: o link substituído não fala da cotação.
 *
 * **O prazo vale o dia inteiro**: `prazoResposta` é data de negócio, e a
 * resposta mandada às 22h do último dia ainda é do prazo.
 */
export function situacaoDoConvite(estado: {
  revogado: boolean;
  ativa: boolean;
  comPedido: boolean;
  prazoResposta: string | null;
  hoje: string;
}): SituacaoConvite {
  if (estado.revogado) return 'SUBSTITUIDO';
  if (estado.comPedido) return 'COM_PEDIDO';
  if (!estado.ativa) return 'COTACAO_INATIVA';
  if (estado.prazoResposta === null) return 'SEM_PRAZO';
  if (estado.prazoResposta < estado.hoje) return 'PRAZO_VENCIDO';
  return 'ABERTO';
}

/**
 * Por que a cotação **não pode ser mandada** agora — ou `null`, quando pode.
 *
 * As mesmas condições que fecham o link, perguntadas antes de abri-lo: mandar um
 * link que já nasceria encerrado seria um e-mail inútil ao fornecedor. A tela
 * apaga o botão com a frase; o servidor recusa com o mesmo texto.
 */
export function motivoParaNaoConvidar(estado: {
  ativa: boolean;
  comPedido: boolean;
  prazoResposta: string | null;
  hoje: string;
}): string | null {
  if (estado.comPedido) return 'A cotação já virou pedido de compra.';
  if (!estado.ativa) return 'A cotação está desativada.';
  if (estado.prazoResposta === null) {
    return 'Informe o prazo de resposta e grave a cotação: é até ele que o fornecedor pode responder.';
  }
  if (estado.prazoResposta < estado.hoje) {
    return 'O prazo de resposta já passou. Estenda o prazo e grave a cotação antes de mandar.';
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* A proposta do fornecedor dentro do `PUT` da cotação                         */
/* -------------------------------------------------------------------------- */

/** A proposta gravada, de volta à forma do payload — para ser regravada como está. */
export function entradaDaPropostaGravada(proposta: PropostaCotacao): PropostaCotacaoEntrada {
  return {
    fornecedorId: proposta.fornecedorId,
    condicaoPagamentoId: proposta.condicaoPagamentoId,
    frete: proposta.frete,
    quantidadeOfertada: proposta.quantidadeOfertada,
    valorUnitario: proposta.valorUnitario,
    valorOutros: proposta.valorOutros,
    valorFrete: proposta.valorFrete,
    valorDesconto: proposta.valorDesconto,
    prazoValidade: proposta.prazoValidade,
    escolhida: proposta.escolhida,
    atendimentos: proposta.atendimentos.map((atendimento) => ({
      solicitacaoCompraItemId: atendimento.solicitacaoCompraItemId,
      quantidadeAtendida: atendimento.quantidadeAtendida,
    })),
    impostos: proposta.impostos,
    ...(proposta.observacao === null ? {} : { observacao: proposta.observacao }),
    conviteCotacaoId: proposta.conviteCotacaoId,
  };
}

/**
 * O que o comprador mandou no `PUT`, com as propostas do fornecedor protegidas.
 *
 * Com o link aberto, a proposta é **do fornecedor**: o comprador não altera o
 * preço que outro digitou. Mas ele precisa marcá-la como escolhida para gerar o
 * pedido, e declarar a cobertura parcial — por isso a regra é por campo, e não
 * pela linha inteira:
 *
 * - **A linha travada que veio no payload** fica com o gravado, menos
 *   `escolhida` e `atendimentos`, que são do comprador. O atendimento que não
 *   couber mais na oferta gravada (o fornecedor baixou a quantidade depois que a
 *   tela abriu) volta ao gravado, em vez de virar recusa por um número que o
 *   comprador não vê.
 * - **A linha travada que não veio** é mantida inteira. É o comprador com a tela
 *   aberta desde antes da resposta: salvar não pode apagar o que chegou.
 * - **A marca de link numa linha que não é travada** só fica se apontar para um
 *   convite que a cotação já tem, do mesmo fornecedor — é a proposta cujo link
 *   fechou e que o comprador agora edita. Qualquer outra marca é descartada:
 *   ela não diria a verdade sobre quem digitou a linha.
 *
 * Pura e no contrato pelo motivo de `valoresDaProposta`: é a regra que decide o
 * que se grava, e é aqui que ela tem teste.
 */
export function mesclarPropostasDoFornecedor(
  enviadas: readonly PropostaCotacaoEntrada[],
  gravadas: readonly PropostaCotacao[],
): PropostaCotacaoEntrada[] {
  const travadas = new Map(
    gravadas
      .filter((p) => p.travadaPeloFornecedor && p.conviteCotacaoId !== null)
      .map((p) => [p.conviteCotacaoId as string, p]),
  );
  const fornecedorDoConvite = new Map(
    gravadas
      .filter((p) => p.conviteCotacaoId !== null)
      .map((p) => [p.conviteCotacaoId as string, p.fornecedorId]),
  );
  const usadas = new Set<string>();

  const mescladas = enviadas.map((enviada) => {
    const marca = enviada.conviteCotacaoId;
    const travada = marca === null ? undefined : travadas.get(marca);
    if (travada) {
      usadas.add(marca as string);
      return comAEscolhaDoComprador(entradaDaPropostaGravada(travada), enviada);
    }
    const marcaValida = marca !== null && fornecedorDoConvite.get(marca) === enviada.fornecedorId;
    return marcaValida ? enviada : { ...enviada, conviteCotacaoId: null };
  });

  for (const [marca, travada] of travadas) {
    if (!usadas.has(marca)) mescladas.push(entradaDaPropostaGravada(travada));
  }
  return mescladas;
}

/**
 * A primeira proposta que repete fornecedor e condição de uma anterior — ou
 * `null`. O contrato já recusa isso no payload; depois da mescla a repetição
 * pode nascer entre a linha que o comprador digitou e a que o fornecedor gravou
 * nesse meio-tempo, na mesma condição.
 */
export function indiceDaPropostaRepetida(
  propostas: readonly { fornecedorId: string; condicaoPagamentoId: string }[],
): number | null {
  const vistas = new Set<string>();
  for (const [indice, proposta] of propostas.entries()) {
    const chave = `${proposta.fornecedorId}·${proposta.condicaoPagamentoId}`;
    if (vistas.has(chave)) return indice;
    vistas.add(chave);
  }
  return null;
}

/** O gravado, com a escolha e o atendimento que o comprador mandou — se couber. */
function comAEscolhaDoComprador(
  gravada: PropostaCotacaoEntrada,
  enviada: PropostaCotacaoEntrada,
): PropostaCotacaoEntrada {
  const atendido = somarQuantidade(enviada.atendimentos.map((a) => a.quantidadeAtendida));
  const cabe = compararQuantidade(atendido, gravada.quantidadeOfertada) <= 0;

  return {
    ...gravada,
    escolhida: enviada.escolhida,
    atendimentos: cabe ? enviada.atendimentos : gravada.atendimentos,
  };
}

/* -------------------------------------------------------------------------- */
/* O envio, do lado do comprador                                              */
/* -------------------------------------------------------------------------- */

/** Teto de fornecedores num envio — a mesma ordem de grandeza das propostas. */
export const MAXIMO_CONVITES_POR_ENVIO = 30;

/**
 * Para quem mandar. **Só ids**: o e-mail é o do cadastro de cada fornecedor,
 * lido no envio, e o fornecedor tem de estar cadastrado — não há endereço
 * avulso. Quem quer outro endereço corrige o cadastro, pelo atalho da tela.
 */
export const enviarConvitesSchema = z
  .object({
    fornecedores: z
      .array(uuidSchema)
      .min(1, 'Marque ao menos um fornecedor.')
      .max(
        MAXIMO_CONVITES_POR_ENVIO,
        `No máximo ${MAXIMO_CONVITES_POR_ENVIO} fornecedores por envio.`,
      ),
  })
  .superRefine((dados, ctx) => {
    const vistos = new Set<string>();
    dados.fornecedores.forEach((id, indice) => {
      if (vistos.has(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fornecedores', indice],
          message: 'Este fornecedor já está na lista.',
        });
      }
      vistos.add(id);
    });
  });

export type EnviarConvites = z.infer<typeof enviarConvitesSchema>;

/** Um envio, como o histórico da cotação o mostra. */
export const conviteCotacaoSchema = z.object({
  id: uuidSchema,
  fornecedorId: uuidSchema,
  fornecedorNome: z.string().nullable(),
  para: z.string(),
  situacaoEnvio: situacaoEnvioSchema,
  erroEnvio: z.string().nullable(),
  enviadoEm: dataHoraSchema,
  enviadoPorNome: z.string().nullable(),
  respondidoEm: dataHoraSchema.nullable(),
  situacao: situacaoConviteSchema,
});

export type ConviteCotacao = z.infer<typeof conviteCotacaoSchema>;

/**
 * Por que o fornecedor aparece na sugestão.
 *
 * `GRUPO`: um dos grupos que ele oferece cobre o do insumo (o ramo inteiro,
 * como no recorte do pedido — decisão 87). `SEM_GRUPO`: o cadastro dele não
 * marcou grupo nenhum, e quem não respondeu à pergunta pode fornecer qualquer
 * insumo. A tela mostra os dois, e o primeiro antes: é a indicação mais forte.
 */
export const MOTIVOS_DA_SUGESTAO = ['GRUPO', 'SEM_GRUPO'] as const;
export type MotivoDaSugestao = (typeof MOTIVOS_DA_SUGESTAO)[number];

export const fornecedorSugeridoSchema = z.object({
  fornecedorId: uuidSchema,
  nome: z.string(),
  email: z.string().nullable(),
  motivo: z.enum(MOTIVOS_DA_SUGESTAO),
});

export type FornecedorSugerido = z.infer<typeof fornecedorSugeridoSchema>;

export const convitesDaCotacaoSchema = z.object({
  /** Do mais novo ao mais antigo, os substituídos inclusive. */
  convites: z.array(conviteCotacaoSchema),
  sugeridos: z.array(fornecedorSugeridoSchema),
  /** `motivoParaNaoConvidar`, resolvido pelo servidor. Nulo quando se pode mandar. */
  motivoParaNaoEnviar: z.string().nullable(),
});

export type ConvitesDaCotacao = z.infer<typeof convitesDaCotacaoSchema>;

/**
 * O resultado de cada fornecedor do envio. Um que falhe não impede os outros:
 * cada e-mail é um pedido à Mensageria, e o fornecedor sem e-mail não deve
 * segurar o que tem.
 */
export const resultadoDosConvitesSchema = z.object({
  resultados: z.array(
    z.object({
      fornecedorId: uuidSchema,
      ok: z.boolean(),
      mensagem: z.string().nullable(),
    }),
  ),
});

export type ResultadoDosConvites = z.infer<typeof resultadoDosConvitesSchema>;

/* -------------------------------------------------------------------------- */
/* A página pública, do lado do fornecedor                                    */
/* -------------------------------------------------------------------------- */

/**
 * O que o fornecedor grava: a proposta inteira, com os impostos.
 *
 * **Quatro campos da proposta não vêm**, e cada um tem dono: `fornecedorId` é
 * o do link, nunca do corpo — senão um fornecedor gravaria proposta em nome de
 * outro; `escolhida` é a decisão do comprador; `atendimentos` é o vínculo com a
 * solicitação, que o servidor monta pela quantidade ofertada, como a tela do
 * comprador faria; e `conviteCotacaoId` é o próprio link.
 */
export const respostaDoFornecedorSchema = propostaCotacaoEntradaSchema
  .omit({ fornecedorId: true, escolhida: true, atendimentos: true, conviteCotacaoId: true })
  .superRefine((proposta, ctx) => {
    // A regra de `validarFreteDasPropostas`, para uma proposta só.
    if (freteTemValor(proposta.frete) || Number(proposta.valorFrete) === 0) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['valorFrete'],
      message:
        'Só o frete CIF tem valor a informar: nele quem entrega é o fornecedor. Zere o valor, ou mude a modalidade para CIF.',
    });
  });

export type RespostaDoFornecedor = z.infer<typeof respostaDoFornecedorSchema>;

/** A proposta já gravada pelo link, para a página abrir preenchida. */
export const propostaDoFornecedorSchema = propostaCotacaoSchema.pick({
  condicaoPagamentoId: true,
  frete: true,
  quantidadeOfertada: true,
  valorUnitario: true,
  valorOutros: true,
  valorFrete: true,
  valorDesconto: true,
  valorTotal: true,
  valorCustoPrevisto: true,
  prazoValidade: true,
  impostos: true,
  observacao: true,
});

export type PropostaDoFornecedor = z.infer<typeof propostaDoFornecedorSchema>;

/**
 * O que a página pública mostra.
 *
 * **Só o que o fornecedor precisa para responder**, e nada da cotação além
 * disso: nem as propostas dos outros, nem a solicitação, nem a obra. A empresa
 * vai pelo nome, o insumo pelo código, nome e unidade — é o que ele tem de
 * reconhecer para dar o preço.
 *
 * As condições de pagamento são as da construtora que servem para compra: é da
 * lista dela que a proposta escolhe, porque a proposta gravada aponta para uma.
 */
export const cotacaoAoFornecedorSchema = z.object({
  empresaNome: z.string(),
  fornecedorNome: z.string(),
  insumo: z.object({
    codigo: z.string(),
    nome: z.string(),
    unidadeSigla: z.string(),
    apenasInteiros: z.boolean(),
    tipo: tipoInsumoSchema,
    casasDecimaisPreco: z.number().int(),
  }),
  quantidadeCotada: z.string(),
  dataCotacao: dataSchema,
  prazoResposta: dataSchema.nullable(),
  observacao: z.string().nullable(),
  situacao: situacaoConviteSchema,
  condicoesPagamento: z.array(
    z.object({ id: uuidSchema, descricao: z.string(), qtdParcelas: z.number().int() }),
  ),
  proposta: propostaDoFornecedorSchema.nullable(),
  respondidoEm: dataHoraSchema.nullable(),
});

export type CotacaoAoFornecedor = z.infer<typeof cotacaoAoFornecedorSchema>;
